import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import { queryKeys } from '../lib/query'
import type { Client, ClientType, Activity, PaginatedResponse, Project } from '@1130-studio/shared'

// Types
interface ClientsFilters {
  type?: ClientType
  search?: string
  page?: number
  pageSize?: number
}

interface CreateClientInput {
  type?: ClientType
  companyName?: string
  inn?: string
  ogrn?: string
  contactName: string
  email?: string
  phone?: string
  telegram?: string
  whatsapp?: string
  legalAddress?: string
  notes?: string
  leadId?: string
}

interface UpdateClientInput {
  type?: ClientType
  companyName?: string | null
  inn?: string | null
  ogrn?: string | null
  contactName?: string
  email?: string | null
  phone?: string | null
  telegram?: string | null
  whatsapp?: string | null
  legalAddress?: string | null
  notes?: string | null
}

// Get clients list
export function useClients(filters: ClientsFilters = {}) {
  return useQuery({
    queryKey: queryKeys.clients.list(filters),
    queryFn: () => {
      const params = new URLSearchParams()
      if (filters.type) params.set('type', filters.type)
      if (filters.search) params.set('search', filters.search)
      if (filters.page) params.set('page', String(filters.page))
      if (filters.pageSize) params.set('pageSize', String(filters.pageSize))

      const queryString = params.toString()
      return api.get<PaginatedResponse<Client>>(`/clients${queryString ? `?${queryString}` : ''}`)
    },
  })
}

// Get single client
export function useClient(id: string) {
  return useQuery({
    queryKey: queryKeys.clients.detail(id),
    queryFn: () => api.get<Client>(`/clients/${id}`),
    enabled: !!id,
  })
}

// Get client activities
export function useClientActivities(id: string) {
  return useQuery({
    queryKey: [...queryKeys.clients.detail(id), 'activities'],
    queryFn: () => api.get<Activity[]>(`/clients/${id}/activities`),
    enabled: !!id,
  })
}

// Get client projects
export function useClientProjects(clientId: string) {
  return useQuery({
    queryKey: [...queryKeys.clients.detail(clientId), 'projects'],
    queryFn: () => api.get<{ items: Project[] }>(`/projects?clientId=${clientId}&pageSize=50`),
    enabled: !!clientId,
  })
}

// Create client
export function useCreateClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateClientInput) => api.post<Client>('/clients', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
    },
  })
}

// Update client
export function useUpdateClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClientInput }) =>
      api.patch<Client>(`/clients/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.detail(variables.id) })
    },
  })
}

// Delete client
export function useDeleteClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => api.delete(`/clients/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
    },
  })
}
